import { createContext, useContext, useState, useEffect } from 'react';

const OptionsContext = createContext(null);

/**
 * Options Provider
 * Manages global options for departments/branches and years
 * These will eventually be fetched from the backend
 */
export function OptionsProvider({ children }) {
  // Predefined options - will be replaced with backend data later
  const [departments, setDepartments] = useState([
    { label: 'All Branches', value: 'All Branches' },
    { label: 'Computer Science', value: 'Computer Science' },
    { label: 'Electronics', value: 'Electronics' },
    { label: 'Mechanical', value: 'Mechanical' },
    { label: 'Civil', value: 'Civil' },
    { label: 'Electrical', value: 'Electrical' },
    { label: 'Aerospace', value: 'Aerospace' },
    { label: 'Chemical', value: 'Chemical' },
  ]);

  const [years, setYears] = useState(() => {
    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: 10 }, (_, i) => {
      const year = currentYear - 4 + i; // 4 past + current + 5 future
      return {
        label: year === currentYear ? `${year} (Current)` : year.toString(),
        value: year.toString(),
      };
    });
    return [{ label: 'All Years', value: 'All Years' }, ...yearOptions];
  });

  const [semesters, setSemesters] = useState([
    {label:'All Semesters',value:'All Semesters'},
    { label: '1st', value: '1st' },
    { label: '2nd', value: '2nd' },
    { label: '3rd', value: '3rd' },
    { label: '4th', value: '4th' },
    { label: '5th', value: '5th' },
    { label: '6th', value: '6th' },
    { label: '7th', value: '7th' },
    { label: '8th', value: '8th' },
  ]);

  const [programmingLanguages, setProgrammingLanguages] = useState([
    { label: 'All Languages', value: 'All Languages' },
    { label: 'CPP', value: 'cpp' },
    { label: 'C', value: 'c' },
    { label: 'Python', value: 'python' },
    { label: 'Java', value: 'java' },
    { label: 'JavaScript', value: 'javascript' },

  ]);

  const [databases, setDatabases] = useState([
    { label: 'All Databases', value: 'All Databases' },
    { label: 'SQL', value: 'sql' },
    { label: 'NoSQL', value: 'nosql' },
  ]);
  const [assessmentStatuses] = useState([
    { label: 'All Status', value: 'All Status' },
    { label: 'Completed', value: 'completed' },
    { label: 'Pending', value: 'pending' },
    { label: 'Not Started', value: 'not_started' },
    { label: 'Incomplete', value: 'incomplete left' },

  ]);

  const [aptitudeLevels] = useState([
    { label: 'Aptitude - All', value: 'All' },
    { label: 'Weak', value: 'Weak' },
    { label: 'Developing', value: 'Developing' },
    { label: 'Adequate', value: 'Adequate' },
    { label: 'Strong', value: 'Strong' },
    { label: 'Outstanding', value: 'Outstanding' },
  ]);

  const [communicationLevels] = useState([
    { label: 'Communication - All', value: 'All' },
    { label: 'A1/A2', value: 'A1 / A2' },
    { label: 'B1', value: 'B1' },
    { label: 'B2', value: 'B2' },
    { label: 'C1/C2', value: 'C1 / C2' },
  ]);

  const [programmingLevels] = useState([
    { label: 'Programming - All', value: 'All' },
    { label: 'Beginner', value: 'Beginner' },
    { label: 'Intermediate', value: 'Intermediate' },
    { label: 'Advanced', value: 'Advanced' },
  ]);

  const [databaseLevels] = useState([
    { label: 'Database - All', value: 'All' },
    { label: 'Beginner', value: 'Beginner' },
    { label: 'Intermediate', value: 'Intermediate' },
    { label: 'Advanced', value: 'Advanced' },
  ]);

  const [overallStatuses] = useState([
    { label: 'Overall - All', value: 'All' },
    { label: 'Interview Ready', value: 'Interview Ready' },
    { label: 'Developmental', value: 'Developmental' },
    { label: 'Needs Training', value: 'Needs Training' },
  ]);

  const getLabel = (list, value) => {
    const item = list.find(i => i.value === value);
    return item ? item.label : value;
  };

  // TODO: Replace this with actual backend API call
  // useEffect(() => {
  //   const fetchOptions = async () => {
  //     try {
  //       const response = await fetchOptionsFromBackend();
  //       if (response.success) {
  //         setDepartments(response.data.departments);
  //         setYears(response.data.years);
  //         setSemesters(response.data.semesters);
  //       }
  //     } catch (error) {
  //       console.error('Error fetching options:', error);
  //     }
  //   };
  //   fetchOptions();
  // }, []);

  const value = {
    departments, setDepartments,
    years, setYears,
    semesters, setSemesters,
    programmingLanguages, setProgrammingLanguages,
    databases, setDatabases,
    assessmentStatuses,
    aptitudeLevels,
    communicationLevels,
    programmingLevels,
    databaseLevels,
    overallStatuses,
    getDepartmentLabel: (v) => getLabel(departments, v),
    getYearLabel: (v) => getLabel(years, v),
    getSemesterLabel: (v) => getLabel(semesters, v),
  };

  return (
    <OptionsContext.Provider value={value}>
      {children}
    </OptionsContext.Provider>
  );
}

/**
 * Hook to use options context
 * @returns {Object} Options context value
 */
export function useOptions() {
  const context = useContext(OptionsContext);
  if (!context) {
    throw new Error('useOptions must be used within OptionsProvider');
  }
  return context;
}

